import { useDisclosure } from "@msuite/ui/hooks";
import { moment } from "@msuite/utils/date";
import { useTranslation } from "@msuite/ui/localization";
import { queries } from "@msuite/zero";
import { useZeroQuery } from "@msuite/zero/react";
import { Crew } from "@/components/plan";
import { DailyContext } from "./context";
import type { GuestPlan } from "./use-daily";
import { PlanStateTag, Tag } from "@msuite/ui/tag";
import { Tooltip } from "@msuite/ui/tooltip";

/** Props Interface */
interface DailyGuestPlanProps {
	dayIndex: number;
	date: string;
	plan: GuestPlan;
}

/**
 * One other organisation's board, reduced to the columns of ours that stand on it.
 *
 * A crew placed on a partner's board belongs to us — they are our people — but the board itself is
 * theirs, and until this existed we could not see the column at all. What we get is the column and
 * nothing else: the query returns the host's plan with its crews filtered to ours, so the rest of
 * their planning never arrives at the client.
 *
 * Everything of the host's stays read-only. `isInteractionBlocked` is what every control on a crew
 * already reads, so setting it here disables the ticket context menus, the vehicle drops and the
 * crew menu in one move — a board that belongs to someone else is not ours to change. The one
 * exception is staffing our own column: that right belongs to the organisation that owns the crew,
 * whoever's board it stands on, so the crew derives its staffing lock from crew ownership rather
 * than from this flag. The mutator and the database enforce the same rule.
 *
 * The host's publish state does not reach that lock either. A partner cannot pull a published day
 * back and usually names its people after the host has handed the day out, so a published guest
 * board stays staffable while every other control on it stays shut.
 */
export function DailyGuestPlan({ dayIndex, date, plan }: DailyGuestPlanProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const disclosure = useDisclosure({ defaultIsOpen: true });
	const showTicketsDisclosure = useDisclosure({ defaultIsOpen: true });
	const vehicleDisclosure = useDisclosure();
	// The board's owner, so the section is titled with the company the board belongs to. The
	// generic label only bridges the moment before the organisation row has synced.
	const [hostOrganization] = useZeroQuery(queries.organizations.byId({ orgId: plan.orgId }));

	/** Guard */
	if (!plan.crews.length) return null;

	/** Render */
	return (
		<DailyContext.Provider
			value={{
				dayIndex,
				disclosure,
				date,
				isInteractionBlocked: true,
				// The board is somebody else's, so no rule of the caller's arranges it — the values
				// are false for the same reason the block above is true. Ticket placement included:
				// a host's work is arranged by the host, whoever's project it names.
				canArrangeBoard: false,
				canArrangeTicketOf: () => false,
				// Nor does the rule list decide anything on a guest board, so there is no third
				// state to be in: the answer is settled by whose board this is.
				rulesState: "ready",
				planId: plan.id,
				plan,
				showTicketsDisclosure,
				vehicleDisclosure,
			}}
		>
			<div className="h-stack justify-between">
				<Tag color="accent">{hostOrganization?.name ?? t("planning.crews_on_partner_board")}</Tag>
				<Tooltip
					label={
						plan.publishedAt
							? `${t("general.last_updated")}: ${moment(plan.publishedAt).longFormat()}`
							: t("planning.day_not_published_yet")
					}
				>
					<PlanStateTag
						state={plan.state}
						variant="default"
					/>
				</Tooltip>
			</div>
			{plan.crews.map((crew) => (
				<Crew
					key={crew.id}
					crewId={crew.id}
				/>
			))}
		</DailyContext.Provider>
	);
}
